import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../../../../../../shared/types/ApiCallType'
import { type RecurringFrequencyType } from '../../../../../../shared/types/RecurringFrequencyType'
import { resetProxy } from '../../../../../../shared/utils/proxyUtils'
import { type GlobalCustomerType } from '../../../../../../shared/types/GlobalCustomerType'
import { type GlobalCustomerCardType } from '../../../../../../shared/types/GlobalCustomerCardType'
import { type GlobalCustomerAchType } from '../../../../../../shared/types/GlobalCustomerAchType'
import { PaymentRadioValues } from '../../../../virtual-terminal/index/PaymentRadioValues'
import { fieldsRequiredForSubmission, PAYMENT_METHOD_FIELD_REQUIRED_FOR_SUBMISSION } from './validators'
import { RecurringPaymentEditProxyFormFieldNamesEnum } from './recurringPaymentEditProxyFormFieldNamesEnum'

interface FormType {
  values: { [key in RecurringPaymentEditProxyFormFieldNamesEnum]?: RecurringFrequencyType | string | Date | number | null }
  errors: { [key in RecurringPaymentEditProxyFormFieldNamesEnum]?: string | null }
  dirty: readonly RecurringPaymentEditProxyFormFieldNamesEnum[]
  blurred: readonly RecurringPaymentEditProxyFormFieldNamesEnum[]
}

export interface RecurringConfigurationEditProxyType {
  displaySlideOver: boolean
  allowSubmit: boolean
  apiCallEditConfiguration: ApiCallType
  apiCall: ApiCallType
  lastTransactionId: string | null
  completed: boolean
  customer: GlobalCustomerType | null
  customerCard: GlobalCustomerCardType | null
  achAccount: GlobalCustomerAchType | null
  form: FormType
  showEndDateError: boolean
  showAmountError: boolean
  showInvoiceNumberError: boolean
  showStreetAddressOneError: boolean
  showCityError: boolean
  showStateError: boolean
  showPostalCodeError: boolean
  showCountryCodeError: boolean
  showEmailError: boolean
  stopOnError: boolean
}

const initialValues = {
  achAccount: null,
  apiCall: {
    error: null,
    loading: false
  },
  apiCallEditConfiguration: {
    error: null,
    loading: false
  },
  completed: false,
  customer: null,
  customerCard: null,
  displaySlideOver: false,
  form: {
    blurred: [],
    dirty: [],
    errors: {},
    values: {}
  },
  lastTransactionId: null,
  paymentRadioValue: PaymentRadioValues.CreditCard,
  stopOnError: true
}

const baseProxy = proxy(initialValues)

const showErrorFunctionGenerator = (fieldName: RecurringPaymentEditProxyFormFieldNamesEnum) => (get: any) => {
  const { form: { dirty, blurred, errors } } = get(baseProxy)
  const isDirty = (dirty as string[]).includes(fieldName)
  const isBlurred = (blurred as string[]).includes(fieldName)
  const isErrored = Boolean((errors as { [key in RecurringPaymentEditProxyFormFieldNamesEnum]?: string | null })[fieldName])
  return isDirty && isBlurred && isErrored
}

export const recurringPaymentEditProxy = derive({
  allowSubmit: (get) => {
    const { form: { dirty, errors } } = get(baseProxy)
    const noErrors = Object.values(errors).filter(x => x).length === 0
    const requiredFields = fieldsRequiredForSubmission()
    // @ts-expect-error
    const idFields = PAYMENT_METHOD_FIELD_REQUIRED_FOR_SUBMISSION.some((y: string) => dirty.includes(y))
    // @ts-expect-error
    const allDirtied = requiredFields.every((x: string) => dirty.includes(x))
    return noErrors && allDirtied && idFields
  },
  showAmountError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.Amount),
  showCityError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.City),
  showCountryCodeError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.CountryCode),
  showEmailError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.Email),
  showEndDateError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.EndDate),
  showInvoiceNumberError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.InvoiceNumber),
  showPostalCodeError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.PostalCode),
  showStateError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.State),
  showStreetAddressOneError: showErrorFunctionGenerator(RecurringPaymentEditProxyFormFieldNamesEnum.AddressOne)
}, { proxy: baseProxy }) as RecurringConfigurationEditProxyType

devtools(recurringPaymentEditProxy, { name: 'recurringPaymentEditProxy' })

export const clearApiErrors = (): void => {
  recurringPaymentEditProxy.apiCallEditConfiguration.error = null
}

export const resetRecurringPaymentEditProxy = (): void => {
  resetProxy(initialValues, recurringPaymentEditProxy)
}
